/**
 * DME & Supplies Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Package, Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function DMESuppliesOverlay({ patientId, patientName, onSave, onClose }) {
  const [formData, setFormData] = useState({
    order_type: "dme",
    items: [{ name: "", quantity: "1", priority: "routine" }],
    delivery_address: "",
    special_instructions: "",
    physician_order_attached: false,
    urgent: false,
  });

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { name: "", quantity: "1", priority: "routine" }]
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const handleSave = () => {
    if (formData.items.some(item => !item.name)) {
      toast.error("Please fill in all item names");
      return;
    }
    toast.success("Order submitted successfully");
    if (onSave) onSave(formData);
    if (onClose) onClose();
  };

  const commonDME = [
    "Hospital Bed", "Wheelchair", "Walker", "Bedside Commode", "Oxygen Concentrator",
    "Nebulizer", "Suction Machine", "Hoyer Lift", "Alternating Pressure Mattress",
    "CPAP/BiPAP", "IV Pole", "Shower Chair"
  ];

  const commonSupplies = [
    "Incontinence Briefs", "Bed Pads (Chux)", "Gloves", "Wound Care Kit",
    "Gauze", "Tape", "Catheter Supplies", "Feeding Supplies",
    "Syringes", "Alcohol Swabs", "Barrier Cream", "Skin Cleanser"
  ];

  return (
    <div className="space-y-4 p-4" data-testid="dme-supplies-overlay">
      <div className="flex items-center gap-2 mb-4">
        <Package className="w-5 h-5 text-slate-400" />
        <h3 className="font-semibold text-white">DME & Supplies Order</h3>
        <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Order Type</Label>
        <Select value={formData.order_type} onValueChange={(v) => setFormData({...formData, order_type: v})}>
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="dme">Durable Medical Equipment (DME)</SelectItem>
            <SelectItem value="supplies">Medical Supplies</SelectItem>
            <SelectItem value="both">Both DME & Supplies</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Common Items (click to add)</Label>
        <div className="flex flex-wrap gap-1.5">
          {(formData.order_type === "supplies" ? commonSupplies : commonDME).slice(0, 8).map((item) => (
            <Badge 
              key={item} 
              variant="outline" 
              className="cursor-pointer border-slate-600 text-slate-300 hover:bg-slate-700"
              onClick={() => {
                const hasEmpty = formData.items.some(i => !i.name);
                if (hasEmpty) {
                  const emptyIndex = formData.items.findIndex(i => !i.name);
                  updateItem(emptyIndex, "name", item);
                } else {
                  setFormData({
                    ...formData,
                    items: [...formData.items, { name: item, quantity: "1", priority: "routine" }]
                  });
                }
              }}
            >
              + {item}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-slate-200">Order Items</Label>
          <Button variant="ghost" size="sm" onClick={addItem} className="text-teal-400 hover:text-teal-300">
            <Plus className="w-4 h-4 mr-1" />Add Item
          </Button>
        </div>
        
        {formData.items.map((item, index) => (
          <div key={index} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-5">
              <Input 
                value={item.name} 
                onChange={(e) => updateItem(index, "name", e.target.value)} 
                placeholder="Item name" 
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div className="col-span-2">
              <Input 
                type="number" 
                value={item.quantity} 
                onChange={(e) => updateItem(index, "quantity", e.target.value)} 
                placeholder="Qty" 
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="col-span-4">
              <Select value={item.priority} onValueChange={(v) => updateItem(index, "priority", v)}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="stat">STAT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1">
              {formData.items.length > 1 && (
                <Button variant="ghost" size="icon" onClick={() => removeItem(index)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Delivery Address</Label>
        <Textarea 
          value={formData.delivery_address} 
          onChange={(e) => setFormData({...formData, delivery_address: e.target.value})} 
          placeholder="Patient address for delivery..." 
          rows={2} 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Special Instructions</Label>
        <Textarea 
          value={formData.special_instructions} 
          onChange={(e) => setFormData({...formData, special_instructions: e.target.value})} 
          placeholder="Any special delivery or setup instructions..." 
          rows={2} 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
          <div>
            <Label className="text-slate-200">Physician Order</Label>
            <p className="text-xs text-slate-400">MD order attached?</p>
          </div>
          <Switch checked={formData.physician_order_attached} onCheckedChange={(c) => setFormData({...formData, physician_order_attached: c})} />
        </div>
        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
          <div>
            <Label className="text-slate-200">Urgent Order?</Label>
            <p className="text-xs text-slate-400">Expedite processing</p>
          </div>
          <Switch checked={formData.urgent} onCheckedChange={(c) => setFormData({...formData, urgent: c})} />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
        <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700"><Save className="w-4 h-4 mr-1" />Submit Order</Button>
      </div>
    </div>
  );
}

export default DMESuppliesOverlay;
